import { formatIsoDate, hoursBetweenTime } from './date';

export type DayKey =
  | 'monday'
  | 'tuesday'
  | 'wednesday'
  | 'thursday'
  | 'friday'
  | 'saturday'
  | 'sunday';

export type DayHours = {
  from: string;
  to: string;
};

export type WeeklyHours = Record<DayKey, DayHours[]>;

export type ScheduleFormValues = {
  startDate: Date;
  weeklyHours: WeeklyHours;
};

export type ScheduleLabels = {
  from: string;
  to: string;
  days: Record<DayKey, string>;
};

type WorkingTimeDay = {
  dewt_weekday: number;
  dewt_start_time: string;
  dewt_end_time: string;
};

type WorkingTimeSchedule = {
  dwts_start: string;
  days: WorkingTimeDay[];
};

export const DAY_KEYS: DayKey[] = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];

export const VIEW_WEEKDAYS = [
  { key: 'monday', weekday: 1 },
  { key: 'tuesday', weekday: 2 },
  { key: 'wednesday', weekday: 3 },
  { key: 'thursday', weekday: 4 },
  { key: 'friday', weekday: 5 },
] as const;

export function createEmptyWeeklyHours(): WeeklyHours {
  return DAY_KEYS.reduce<WeeklyHours>((acc, day) => {
    acc[day] = [];
    return acc;
  }, {} as WeeklyHours);
}

export function createEmptyDayHours(): DayHours {
  return { from: '', to: '' };
}

export function formatScheduleDate(value: string): string {
  const [year, month, day] = value.split('-');

  if (!year || !month || !day) {
    return value;
  }

  return `${day}.${month}.${year}`;
}

function formatScheduleTime(value: string | null): string | null {
  if (!value?.trim()) {
    return null;
  }

  return value.slice(0, 5);
}

export function getDayIntervals(days: WorkingTimeDay[]): string[] {
  return days
    .map((day) => ({
      from: formatScheduleTime(day.dewt_start_time),
      to: formatScheduleTime(day.dewt_end_time),
    }))
    .filter((interval): interval is { from: string; to: string } =>
      Boolean(interval.from && interval.to),
    )
    .sort((first, second) => first.from.localeCompare(second.from))
    .map((interval) => `${interval.from} - ${interval.to}`);
}

export function addDays(date: Date, days: number): Date {
  const nextDate = new Date(date);
  nextDate.setDate(nextDate.getDate() + days);
  nextDate.setHours(0, 0, 0, 0);
  return nextDate;
}

export function todayDate(): Date {
  return addDays(new Date(), 0);
}

export function maxDate(firstDate: Date, secondDate: Date): Date {
  return firstDate > secondDate ? firstDate : secondDate;
}

export function dateFromDto(value: string | null | undefined): Date {
  if (!value) {
    return todayDate();
  }

  const [year, month, day] = value.split('-').map(Number);

  if (!year || !month || !day) {
    return todayDate();
  }

  return new Date(year, month - 1, day);
}

export function isScheduleDateOnOrBeforeToday(value: string): boolean {
  return formatIsoDate(dateFromDto(value)) <= formatIsoDate(todayDate());
}

export function getScheduleMinStartDate(
  currentSchedule: WorkingTimeSchedule | null,
): Date {
  if (!currentSchedule) {
    return todayDate();
  }

  return addDays(dateFromDto(currentSchedule.dwts_start), 1);
}

// Reduces a time value to `HH:mm`; anything that is not text is no time.
export function normalizeScheduleTime(value: unknown): string {
  return typeof value === 'string' ? value.trim().slice(0, 5) : '';
}

export function scheduleToWeeklyHours(
  schedule: WorkingTimeSchedule | null,
): WeeklyHours {
  const weeklyHours = createEmptyWeeklyHours();

  schedule?.days.forEach((day) => {
    const weekday = VIEW_WEEKDAYS.find(
      (item) => item.weekday === day.dewt_weekday,
    );

    if (!weekday) {
      return;
    }

    const from = normalizeScheduleTime(day.dewt_start_time);
    const to = normalizeScheduleTime(day.dewt_end_time);
    if (from && to) {
      weeklyHours[weekday.key].push({ from, to });
    }
  });

  VIEW_WEEKDAYS.forEach(({ key }) => {
    weeklyHours[key].sort((first, second) =>
      first.from.localeCompare(second.from),
    );
  });

  return weeklyHours;
}

function normalizeWeeklyHours(weeklyHours: WeeklyHours) {
  return VIEW_WEEKDAYS.map(({ key }) => ({
    key,
    intervals: (weeklyHours[key] ?? [])
      .map((interval) => ({
        from: normalizeScheduleTime(interval.from),
        to: normalizeScheduleTime(interval.to),
      }))
      .filter((interval) => interval.from || interval.to)
      .sort((first, second) => first.from.localeCompare(second.from)),
  }));
}

function weeklyHoursMatchSchedule(
  weeklyHours: WeeklyHours,
  schedule: WorkingTimeSchedule | null,
): boolean {
  return (
    JSON.stringify(normalizeWeeklyHours(weeklyHours)) ===
    JSON.stringify(normalizeWeeklyHours(scheduleToWeeklyHours(schedule)))
  );
}

export type IntervalFieldError = 'required' | 'endAfterStart';

export type IntervalValidation = {
  fieldErrors: Map<string, IntervalFieldError>;
  overlappingIntervalKeys: Set<string>;
};

export function getDayIntervalValidation(
  dayKey: DayKey,
  intervals: WeeklyHours[DayKey],
): IntervalValidation {
  const fieldErrors = new Map<string, IntervalFieldError>();
  const overlappingIntervalKeys = new Set<string>();
  const normalizedIntervals = intervals.map((interval) => ({
    from: normalizeScheduleTime(interval.from),
    to: normalizeScheduleTime(interval.to),
  }));

  normalizedIntervals.forEach((interval, index) => {
    const intervalKey = `${dayKey}.${index}`;
    // An interval with no times is the unused "add working hours" slot: it
    // is not validated and is left out on save.
    if (!interval.from && !interval.to) {
      return;
    }
    if (!interval.from) {
      fieldErrors.set(`${intervalKey}.from`, 'required');
    }
    if (!interval.to) {
      fieldErrors.set(`${intervalKey}.to`, 'required');
    } else if (interval.from && interval.from >= interval.to) {
      fieldErrors.set(`${intervalKey}.to`, 'endAfterStart');
    }
  });

  normalizedIntervals.forEach((interval, index) => {
    normalizedIntervals.slice(index + 1).forEach((other, offset) => {
      if (
        interval.from &&
        interval.to &&
        other.from &&
        other.to &&
        interval.from < other.to &&
        other.from < interval.to
      ) {
        overlappingIntervalKeys.add(`${dayKey}.${index}`);
        overlappingIntervalKeys.add(`${dayKey}.${index + offset + 1}`);
      }
    });
  });

  return { fieldErrors, overlappingIntervalKeys };
}

export function getWeeklyIntervalValidation(
  weeklyHours: WeeklyHours,
): IntervalValidation {
  const fieldErrors = new Map<string, IntervalFieldError>();
  const overlappingIntervalKeys = new Set<string>();
  VIEW_WEEKDAYS.forEach(({ key }) => {
    const dayValidation = getDayIntervalValidation(key, weeklyHours[key] ?? []);
    dayValidation.fieldErrors.forEach((error, fieldName) => {
      fieldErrors.set(fieldName, error);
    });
    dayValidation.overlappingIntervalKeys.forEach((intervalKey) => {
      overlappingIntervalKeys.add(intervalKey);
    });
  });
  return { fieldErrors, overlappingIntervalKeys };
}

export function getFirstInvalidDay(
  validation: IntervalValidation,
): DayKey | undefined {
  const invalidKeys = [
    ...validation.fieldErrors.keys(),
    ...validation.overlappingIntervalKeys,
  ];
  return VIEW_WEEKDAYS.find(({ key }) =>
    invalidKeys.some((invalidKey) => invalidKey.startsWith(`${key}.`)),
  )?.key;
}

export function hasInvalidIntervals(weeklyHours: WeeklyHours): boolean {
  const { fieldErrors, overlappingIntervalKeys } =
    getWeeklyIntervalValidation(weeklyHours);
  return fieldErrors.size > 0 || overlappingIntervalKeys.size > 0;
}

export function calculateTotalHours(weeklyHours: WeeklyHours): number {
  return normalizeWeeklyHours(weeklyHours).reduce(
    (total, { intervals }) =>
      total +
      intervals.reduce((dayHours, interval) => {
        if (!interval.from || !interval.to || interval.from >= interval.to) {
          return dayHours;
        }

        return dayHours + hoursBetweenTime(interval.from, interval.to);
      }, 0),
    0,
  );
}

export function calculateDayHours(intervals: DayHours[]): number {
  return intervals.reduce((total, interval) => {
    const from = normalizeScheduleTime(interval.from);
    const to = normalizeScheduleTime(interval.to);
    if (!from || !to || from >= to) {
      return total;
    }

    return total + hoursBetweenTime(from, to);
  }, 0);
}

export function formatWorkingHours(hours: number): string {
  const minutes = Math.round(hours * 60);
  const wholeHours = Math.floor(minutes / 60);
  const remainingMinutes = Math.abs(minutes % 60);
  return `${wholeHours}:${remainingMinutes.toString().padStart(2, '0')}`;
}

export function durationToHours(value: string | null | undefined): number {
  if (!value) {
    return 0;
  }

  const match = value
    .trim()
    .match(/^(?:(\d+)\s+)?(\d+):(\d+)(?::\d+(?:\.\d+)?)?$/);
  if (!match) {
    return 0;
  }

  const [, days = '0', hours = '0', minutes = '0'] = match;
  return Number(days) * 24 + Number(hours) + Number(minutes) / 60;
}

export function scheduleFormToPayload(values: ScheduleFormValues) {
  const days = VIEW_WEEKDAYS.flatMap(({ key, weekday }) =>
    (values.weeklyHours[key] ?? [])
      .map((interval) => ({
        from: normalizeScheduleTime(interval.from),
        to: normalizeScheduleTime(interval.to),
      }))
      .filter((interval) => interval.from && interval.to)
      .sort((first, second) => first.from.localeCompare(second.from))
      .map((interval) => ({
        dewt_weekday: weekday,
        dewt_start_time: interval.from,
        dewt_end_time: interval.to,
      })),
  );

  return {
    dwts_start: formatIsoDate(values.startDate),
    days,
  };
}

export function isUnchangedSchedule(
  values: ScheduleFormValues,
  currentSchedule: WorkingTimeSchedule | null,
  futureSchedule: WorkingTimeSchedule | null,
): boolean {
  if (futureSchedule) {
    return (
      formatIsoDate(values.startDate) === futureSchedule.dwts_start &&
      weeklyHoursMatchSchedule(values.weeklyHours, futureSchedule)
    );
  }

  return weeklyHoursMatchSchedule(values.weeklyHours, currentSchedule);
}
